const NUMBER_OPERATORS = ["=", "≥", "≤", ">", "<"];

export const COMMISSION_CONDITION_FIELDS = [
  { value: "Initial Payment Currency", label: "Initial Payment Currency", valueType: "currency" },
  { value: "Initial Payment", label: "Initial Payment", valueType: "number" },
  { value: "Insured Age", label: "Insured Age", valueType: "number", aliases: ["Age"] },
  { value: "Contribution Period", label: "Contribution Period", valueType: "number" },
  { value: "Guarantee Period", label: "Guarantee Period", valueType: "number" },
  { value: "Income Period", label: "Income Period", valueType: "number" },
  { value: "Product Year", label: "Product Year", valueType: "number" },
  { value: "Pay Until Age", label: "Pay Until Age", valueType: "number" },
  { value: "Pay Term", label: "Pay Term", valueType: "number" },
  { value: "Pay Mode", label: "Pay Mode", valueType: "payMode", aliases: ["Frequency"] },
  { value: "Sum Insured", label: "Sum Insured", valueType: "number", aliases: ["Sum Assured"] },
  { value: "Premium", label: "Premium", valueType: "number" },
  { value: "Currency", label: "Currency", valueType: "currency" },
  { value: "Single Premium", label: "Single Premium", valueType: "none" },
  { value: "Custom", label: "Custom", valueType: "custom" },
];

const KEYWORD_MATCHERS = COMMISSION_CONDITION_FIELDS.filter(
  (field) => field.valueType !== "custom" && field.valueType !== "none"
)
  .flatMap((field) => {
    const keywords = [field.value, ...(field.aliases || [])];
    return keywords.map((keyword) => ({ keyword, field }));
  })
  .sort((left, right) => right.keyword.length - left.keyword.length);

export const conditionFieldOptions = COMMISSION_CONDITION_FIELDS.map((field) => ({
  value: field.value,
  label: field.label,
}));

export const getConditionField = (value) =>
  COMMISSION_CONDITION_FIELDS.find((field) => field.value === value) || null;

export const operatorsForField = (fieldValue) => {
  const field = getConditionField(fieldValue);
  if (!field || field.valueType === "none" || field.valueType === "custom") return [];
  if (field.valueType === "number") {
    return NUMBER_OPERATORS.map((operator) => ({ value: operator, label: operator }));
  }
  return [{ value: "=", label: "=" }];
};

export const blankCondition = () => ({
  field: "Insured Age",
  operator: "≥",
  value: "",
  customField: "",
  unrecognized: false,
  raw: "",
});

const normalizeOperator = (operator) => {
  if (operator === ">=") return "≥";
  if (operator === "<=") return "≤";
  return operator;
};

const normalizePayModeValue = (value) => {
  const text = String(value || "").trim();
  if (/^single\s+payment$/i.test(text)) return "LUMPSUM";
  return text;
};

export const parseConditionLine = (line) => {
  const text = String(line || "").trim();
  if (!text || text.toUpperCase() === "ALL") return null;

  if (/^single\s+premium$/i.test(text)) {
    return {
      field: "Single Premium",
      operator: "",
      value: "",
      customField: "",
      unrecognized: false,
      raw: "",
    };
  }

  const customMatch = text.match(/^custom\s*=\s*([^=]+)\s*=\s*(.+)$/i);
  if (customMatch) {
    return {
      field: "Custom",
      operator: "=",
      value: customMatch[2].trim(),
      customField: customMatch[1].trim(),
      unrecognized: false,
      raw: "",
    };
  }

  for (const matcher of KEYWORD_MATCHERS) {
    const pattern = new RegExp(
      `^${matcher.keyword.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s*(>=|<=|≥|≤|=|>|<)\\s*(.+)$`,
      "i"
    );
    const match = text.match(pattern);
    if (!match) continue;

    const field = matcher.field;
    let value = match[2].trim();
    if (field.valueType === "payMode") value = normalizePayModeValue(value);

    return {
      field: field.value,
      operator: field.valueType === "number" ? normalizeOperator(match[1]) : "=",
      value,
      customField: "",
      unrecognized: false,
      raw: "",
    };
  }

  return {
    field: "",
    operator: "",
    value: "",
    customField: "",
    unrecognized: true,
    raw: text,
  };
};

export const parseConditionList = (conditions) => {
  const source = Array.isArray(conditions) ? conditions : [conditions];
  return source
    .flatMap((line) => String(line ?? "").split(/\r?\n/))
    .map((line) => parseConditionLine(line))
    .filter(Boolean);
};

export const serializeCondition = (condition) => {
  if (!condition) return "";
  if (condition.unrecognized) return String(condition.raw || "").trim();

  const field = getConditionField(condition.field);
  if (!field) return "";
  if (field.valueType === "none") return field.value;

  if (field.valueType === "custom") {
    const customField = String(condition.customField || "").trim();
    const value = String(condition.value ?? "").trim();
    if (!customField || !value) return "";
    return `CUSTOM=${customField}=${value}`;
  }

  const value = String(condition.value ?? "").trim();
  if (!value) return "";
  const operator = condition.operator || "=";
  return `${field.value} ${operator} ${value}`;
};

export const serializeConditionList = (conditions) =>
  (conditions || []).map(serializeCondition).map((line) => line.trim()).filter(Boolean);
